/**
 * HoldingPage - Subscription Pending/Expired Page
 * ================================================
 * SESSION 109: SaaS Auth
 * 
 * Shown to users who:
 * - Have not been granted a subscription yet (pending)
 * - Had a subscription that has expired
 * 
 * Features:
 * - Shows appropriate message based on status
 * - Provides logout option
 * - Admin contact information
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, Clock, AlertTriangle, Mail } from 'lucide-react';

export const HoldingPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Determine the status
  const isExpired = user?.subscription_end_date && new Date(user.subscription_end_date) < new Date();
  const isPending = !user?.is_active && !isExpired;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4" 
      style={{ background: '#0a0a0f' }}
      data-testid="holding-page"
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
              <span className="text-cyan-400 font-bold text-sm">AG</span>
            </div>
            <span className="text-xl font-bold text-white tracking-tight">AlgoGreeks</span>
          </div>
        </div>

        {/* Status Card */}
        <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6 backdrop-blur-sm">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            {isExpired ? (
              <div className="w-16 h-16 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-amber-400" />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                <Clock className="w-8 h-8 text-blue-400" />
              </div>
            )}
          </div>

          {/* Message */}
          <div className="text-center mb-6">
            <h2 className="text-lg font-semibold text-white mb-2">
              {isExpired ? 'Subscription Expired' : 'Access Pending'}
            </h2>
            <p className="text-sm text-gray-400">
              {isExpired 
                ? 'Your subscription has expired. Please contact the administrator to renew your access.'
                : 'Your account is pending approval. The administrator will grant access shortly.'
              }
            </p>
          </div>

          {/* User Info */}
          <div className="bg-gray-800/50 rounded-lg p-3 mb-6">
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name || user.email} 
                  className="w-10 h-10 rounded-full border border-gray-700"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                  <span className="text-gray-400 text-sm font-medium">
                    {(user?.name || user?.email || 'U')[0].toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'User'}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            {isExpired && user?.subscription_end_date && (
              <p className="text-xs text-amber-400/80 mt-2">
                Expired on: {new Date(user.subscription_end_date).toLocaleString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                  timeZone: 'Asia/Kolkata'
                })} IST
              </p>
            )}
          </div>

          {/* Contact Admin */}
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
            <Mail className="w-4 h-4" />
            <span>Contact: navneetbihani@gmail.com</span>
          </div>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            data-testid="holding-logout-btn"
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg
                       bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium text-sm
                       transition-all duration-200 border border-gray-700 hover:border-gray-600"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>

        {/* Footer */}
        <p className="text-[10px] text-gray-700 text-center mt-6">
          AlgoGreeks HFT Engine v2.0
        </p>
      </div>
    </div>
  );
};

export default HoldingPage;
